import L from "leaflet";

export const TOOLTIP_OPEN_DELAY_MS = 1000;

type TooltipCapableLayer = L.Layer & {
  bindTooltip: (
    content: string,
    options?: L.TooltipOptions
  ) => TooltipCapableLayer;
  closeTooltip: () => TooltipCapableLayer | undefined;
  getTooltip?: () => L.Tooltip | undefined;
  openTooltip: (latlng?: L.LatLng) => TooltipCapableLayer;
  _initTooltipInteractions?: (remove?: boolean) => void;
};

interface BindDelayedHoverTooltipParams {
  layer: L.Layer;
  content: string;
  tooltipOptions: L.TooltipOptions;
  isLineLayer?: boolean;
  delayMs?: number;
  getOpenLatLng?: (event: L.LeafletMouseEvent) => L.LatLng | undefined;
}

export function createDelayedTooltipController(
  delayMs = TOOLTIP_OPEN_DELAY_MS
) {
  let openTimer: ReturnType<typeof setTimeout> | null = null;

  function cancelPending() {
    if (openTimer) {
      clearTimeout(openTimer);
      openTimer = null;
    }
  }

  function scheduleOpen(openTooltip: () => void) {
    cancelPending();

    openTimer = setTimeout(() => {
      openTimer = null;
      openTooltip();
    }, delayMs);
  }

  function cancelAndClose(closeTooltip: () => void) {
    cancelPending();
    closeTooltip();
  }

  return {
    cancelAndClose,
    cancelPending,
    scheduleOpen,
  };
}

export function bindDelayedHoverTooltip({
  layer,
  content,
  tooltipOptions,
  isLineLayer = false,
  delayMs = TOOLTIP_OPEN_DELAY_MS,
  getOpenLatLng,
}: BindDelayedHoverTooltipParams) {
  const tooltipLayer = layer as TooltipCapableLayer;
  const tooltipController = createDelayedTooltipController(delayMs);

  tooltipLayer.bindTooltip(content, tooltipOptions);

  // Leaflet automatically opens non-permanent tooltips on hover. Disable those
  // default listeners so the app-controlled delay is the only hover path.
  tooltipLayer._initTooltipInteractions?.(true);

  tooltipLayer.on("mouseover", (e: L.LeafletMouseEvent) => {
    const latlng = getOpenLatLng?.(e);
    tooltipController.scheduleOpen(() => {
      tooltipLayer.openTooltip(latlng);
    });
  });

  tooltipLayer.on("mouseout", () => {
    tooltipController.cancelAndClose(() => {
      tooltipLayer.closeTooltip();
    });
  });

  tooltipLayer.on("remove", () => {
    tooltipController.cancelPending();
  });

  if (isLineLayer) {
    tooltipLayer.on("mousemove", (e: L.LeafletMouseEvent) => {
      tooltipLayer.getTooltip?.()?.setLatLng(e.latlng);
    });
  }

  return tooltipController;
}
